import type { Transaction } from "@pandas/http-client";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Loader from "@/components/core/Loaders/Loader";
import Header from "@/components/Table/Header";
import { Column, Table } from "@/components/Table/index";
import ExportErrorBanner from "@/components/Transactions/ExportErrorBanner";
import { useTransactionColumnRenderers } from "@/components/Transactions/TransactionColumns";
import { useTransactionExport } from "@/hooks/useTransactionExport";
import { useTransactions } from "@/hooks/useTransactions";
import { requireAuth } from "@/lib/guards";
import { transactionsSearchSchema } from "@/lib/search-params";

export const Route = createFileRoute("/$locale/$partner/transactions")({
	beforeLoad: requireAuth,
	component: TransactionsPage,
	validateSearch: (search) => transactionsSearchSchema.parse(search),
});

function TransactionsPage() {
	const { t } = useTranslation();

	const {
		transactions,
		metadata,
		selectedDates,
		isLoading,
		effectiveLocationId,
		locationId,
		handlePageChange,
		handleFilter,
		handleClearAllFilters,
	} = useTransactions();

	const { hasExportError, isExportDisabled, handleExport, clearExportError } = useTransactionExport({
		effectiveLocationId,
		locationId,
		selectedDates,
	});

	const {
		voucherCodeTemplate,
		voucherStatusTemplate,
		methodTemplate,
		priceTemplate,
		startedFromMapperTemplate,
		formatTimestamp,
		deviceTemplate,
	} = useTransactionColumnRenderers();

	if (isLoading) {
		return (
			<div className="absolute w-fit h-fit top-1/3 left-1/2">
				<Loader />
			</div>
		);
	}

	return (
		<div className="mx-auto px-12 container">
			<Header
				title={t("Transactions Table.Title")}
				hasExport={true}
				hasFilters={true}
				selectedDates={selectedDates}
				onFilter={handleFilter}
				onClearAllFilters={handleClearAllFilters}
				onExport={handleExport}
				isExportDisabled={isExportDisabled}
			/>
			<Table<Transaction>
				className="min-w-full table-auto"
				values={transactions}
				noDataText={t("Transactions Table.Empty")}
				totalEntries={metadata.total}
				totalPages={metadata.pages}
				currentPage={metadata.currentPage}
				perPage={metadata.perPage}
				onPageChange={handlePageChange}
			>
				<Column<Transaction>
					header={t("Transactions Table.Session Id")}
					field="session_id"
				/>
				<Column<Transaction>
					header={t("Transactions Table.Voucher Code")}
					field="voucher_code"
					render={voucherCodeTemplate}
				/>
				<Column<Transaction>
					header={t("Transactions Table.Voucher Status")}
					field="voucher_status"
					render={voucherStatusTemplate}
				/>
				<Column<Transaction>
					header={t("Transactions Table.Payment method")}
					field="method"
					render={methodTemplate}
				/>
				<Column<Transaction>
					header={t("Transactions Table.Started From")}
					field="started_from"
					render={startedFromMapperTemplate}
				/>
				<Column<Transaction>
					header={t("Transactions Table.Last Price")}
					field="price"
					render={priceTemplate}
				/>
				<Column<Transaction>
					header={t("Transactions Table.Device")}
					field="product_description"
					render={deviceTemplate}
				/>
				<Column<Transaction>
					header={t("Transactions Table.Date")}
					field="created_at"
					render={formatTimestamp}
				/>
			</Table>
			{hasExportError && <ExportErrorBanner onDismiss={clearExportError} />}
		</div>
	);
}
